import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import ContactForm from "./components/ContactForm";

const services = [
  {
    icon: "ri-newspaper-line",
    title: "最新电影资讯",
    desc: "追踪影院动态、定档预告与票房快讯，让你第一时间掌握影市风向。",
  },
  {
    icon: "ri-movie-2-line",
    title: "分类片库浏览",
    desc: "按类型、地区、年份、评分多条件组合筛选，快速定位想看的影片。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "精选专题片单",
    desc: "编辑部精心策划主题片单，覆盖科幻、悬疑、爱情、动画等多元口味。",
  },
  {
    icon: "ri-file-list-3-line",
    title: "影片资料详情",
    desc: "提供导演、主演、时长、评分与剧情简介等完整资料，辅助观影决策。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "邮箱", value: "hello@555movie.com" },
  { icon: "ri-phone-line", label: "客服热线", value: "400-555-0555" },
  { icon: "ri-map-pin-2-line", label: "办公地址", value: "北京市朝阳区光影文化产业园 A 座" },
  { icon: "ri-time-line", label: "服务时间", value: "周一至周五 09:30 - 18:30" },
];

const socials = [
  { icon: "ri-weibo-fill", label: "微博" },
  { icon: "ri-wechat-fill", label: "微信公众号" },
  { icon: "ri-youtube-fill", label: "视频号" },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 555电影",
    description:
      "555电影是专注电影资讯与优质片单推荐的内容平台，提供最新电影资讯、分类片库浏览、精选专题片单与影片资料，帮助影迷高效选片。",
    keywords: "关于555电影,电影资讯平台,电影推荐网站,观影指南",
  });

  return (
    <div className="w-full animate-fadeIn">
      {/* Hero */}
      <div className="w-full bg-primary-950">
        <div className="mx-auto w-full max-w-screen-xl px-4 py-10 md:px-6 md:py-16">
          <div className="mb-4 text-background-300">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          </div>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-800 px-3 py-1 text-xs font-medium text-accent-400">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-compass-3-line text-xs"></i>
              </span>
              关于 555电影
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-snug text-background-50 md:text-4xl">
              让每一次选片，都更简单
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-background-200 md:text-base">
              555电影是一个面向广大电影爱好者的影视资讯与推荐平台。我们持续追踪最新上映与热门影片动态，用清晰的分类、专业的评分与精心策划的专题，帮助影迷用最少的时间，找到最想看的那部电影。
            </p>
          </div>
        </div>
      </div>

      {/* Services */}
      <div className="mx-auto w-full max-w-screen-xl px-4 py-12 md:px-6 md:py-16">
        <div className="mb-8">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            我们提供什么
          </h2>
          <p className="mt-2 text-sm text-foreground-600">围绕影迷的核心需求，构建实用的内容体系</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s) => (
            <div
              key={s.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className={`${s.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 text-base font-semibold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contact + form */}
      <div className="w-full bg-background-100">
        <div className="mx-auto w-full max-w-screen-xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                联系我们
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                无论你是想补充影片资料、反馈内容问题，还是寻求合作，都欢迎通过这些方式找到我们。
              </p>

              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {contacts.map((c) => (
                  <div
                    key={c.label}
                    className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                      <i className={`${c.icon} text-lg`}></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-500">{c.label}</p>
                      <p className="mt-0.5 break-words text-sm font-medium text-foreground-900">
                        {c.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center gap-3">
                {socials.map((s) => (
                  <span
                    key={s.label}
                    className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className={`${s.icon} text-lg`}></i>
                  </span>
                ))}
              </div>

              <div className="mt-8 rounded-lg border-l-4 border-primary-500 bg-background-50 p-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                  <span className="flex h-4 w-4 items-center justify-center text-primary-500">
                    <i className="ri-shield-check-line text-base"></i>
                  </span>
                  版权与说明
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  本站内容均为资讯整理与示范展示，仅供影迷学习与观影参考，不提供在线播放、下载与会员服务。影片资料版权归原作者及出品方所有，如涉侵权请联系我们删除。
                </p>
              </div>
            </div>

            <div>
              <ContactForm />
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-screen-xl px-4 py-10 text-center md:px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-left-line text-sm"></i>
          </span>
          返回首页
        </Link>
      </div>
    </div>
  );
}